/**
 * LocationPanel Component (New Design System)
 *
 * Redesigned Location management panel using the new design system components.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, FormControlLabel, Checkbox, Typography, Chip, Stack } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

// Design System Components
import {
    colors,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    ActivityCard,
    DraftTextField,
    DraftSelectField,
    DraftAutocompleteField,
    DraftPinField,
    ReadOnlyField,
    Button,
    ConfirmDialog,
} from '@components/design-system';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import {
    useLocationMutation,
    useLocationQuery,
    useNameMapQuery,
    useOrgMapQuery,
    useCountriesQuery,
} from '@features/redux/apis/org';
import { useAuditHistoryQuery } from '@features/redux/apis/audit';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { getIdDisplayValue } from '@utils/index';
import { keymap } from '@utils/helper';
import { TRACKING_TYPE_OPTIONS } from '@consts/company';
import { navigateToActivity } from '@utils/activityLinks';
import { LocationEffects } from './LocationEffects';
import { LocationSystems } from './LocationSystems';
import { AppRoutes } from '@consts/routes';

// Legacy components still needed
import { SingleDatePicker } from '@components/SingleDatePicker';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';
import dayjs from 'dayjs';

// Draft state setup - extended with display fields for dropdowns
type LocationDraft = {
    LocationID: number;
    LocationName: string;
    CompanyID: number;
    CompanyName: string;
    ResellerID: number | null;
    ResellerName: string;
    Country: string;
    PIN: string;
    Inactive: boolean;
    ConsentToSell: string | null;
    CutPlanID: number;
    WarrantyPlanID: number;
    TrackingFlag: number;
};

const useLocations = Query.withDraft(useLocationQuery, {
    LocationName: '',
    LocationID: 0,
    Country: '',
    PIN: '',
    CompanyID: 0,
    CompanyName: '',
    ResellerID: null,
    ResellerName: '',
    Inactive: false,
    ConsentToSell: null,
    WarrantyPlanID: 0,
    CutPlanID: 0,
    TrackingFlag: 1,
} as LocationDraft);

export const LocationPanel: React.FC<ManageProps> = ({ operation, id: locationId, onSuccess }) => {
    const navigate = useNavigate();
    const role = useSelector((state: RootState) => state.auth.Roles.role);
    const auditPk = locationId ? `Location#${locationId}` : null;

    // Fetch location data
    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useLocations({ locationId: +locationId! }, { skip: operation === Operation.Create });
    const [draftState, setDraftState] = draft;

    // Fetch org data for dropdowns
    const { data: nameMap } = useNameMapQuery({});
    const { data: orgMap } = useOrgMapQuery({});
    const { data: countries, isLoading: countriesLoading } = useCountriesQuery({});

    // Mutation
    const [mutate, { isLoading: mutationLoading }] = useLocationMutation({
        fixedCacheKey: operation === Operation.Edit ? 'LocationMutation' : 'LocationCreation',
    });

    // Consent to sell state
    const [cts, setCts] = useState(false);
    useEffect(() => setCts(Boolean(data?.ConsentToSell)), [data?.ConsentToSell]);

    // Archive confirmation dialog state
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

    // Audit history
    const { data: auditData = [] } = useAuditHistoryQuery(
        { entityPk: auditPk! },
        { skip: !auditPk || operation === Operation.Create }
    );

    // Build options for autocompletes
    const companyOptions = useMemo(() => keymap(nameMap?.companies), [nameMap?.companies]);

    const resellerOptions = useMemo(() => {
        const allResellers = keymap(nameMap?.resellers);
        // Filter by selected company
        if (draftState?.CompanyID && orgMap?.companyResellerMap) {
            const validIds = orgMap.companyResellerMap[draftState.CompanyID] || [];
            return allResellers.filter(({ id }) => validIds.includes(+id));
        }
        return allResellers;
    }, [nameMap?.resellers, orgMap?.companyResellerMap, draftState?.CompanyID]);

    const countryOptions = useMemo(
        () => countries?.map(({ countryName }) => ({ id: countryName, name: countryName })) || [],
        [countries]
    );

    const handleSubmit = async () => {
        const result = await mutate({
            method: operation === Operation.Create ? 'POST' : 'PATCH',
            location: draftState,
        });

        // Navigate to edit page after successful creation
        if (operation === Operation.Create && 'data' in result && result.data?.LocationID) {
            navigate(`${AppRoutes.Organizations}?type=location&id=${result.data.LocationID}`);
        } else {
            onSuccess?.();
        }
    };

    const handleArchiveToggle = async () => {
        await mutate({
            method: 'PATCH',
            location: { ...draftState, Inactive: !draftState.Inactive },
        });
        setDraftState((prev) => ({ ...prev, Inactive: !prev.Inactive }));
        setArchiveDialogOpen(false);
    };

    const isLoading = queryLoading || mutationLoading;
    // @ts-expect-error - Validate types don't match exactly
    const isValid = Validate.Org.location(draftState);

    const pageTitle = operation === Operation.Create ? 'Register New Location' : data?.LocationName || 'Location';

    return (
        <>
            <LocationEffects />

            <EntityPageLayout loading={isLoading && !data}>
                {/* Header */}
                <EntityPageHeader
                    entityType='location'
                    title={pageTitle}
                    id={operation !== Operation.Create ? locationId : undefined}
                    icon={<LocationOnIcon />}
                    isCreate={operation === Operation.Create}
                    loading={isLoading && !data}
                    onCancel={onSuccess}
                    onSave={operation !== Operation.View ? handleSubmit : undefined}
                    saveDisabled={!isValid}
                    saving={mutationLoading}
                    statusBadge={
                        draftState.Inactive ? (
                            <Chip
                                size='small'
                                label='Archived'
                                sx={{
                                    backgroundColor: colors.warning.light,
                                    color: colors.warning.dark,
                                    fontWeight: 600,
                                }}
                            />
                        ) : undefined
                    }
                    actions={
                        operation !== Operation.Create && locationId ? (
                            <Stack direction='row' spacing={1}>
                                <Button
                                    variant='secondary'
                                    onClick={() => setArchiveDialogOpen(true)}
                                    startIcon={
                                        draftState.Inactive ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
                                    }>
                                    {draftState.Inactive ? 'Restore' : 'Archive'}
                                </Button>
                                <Button variant='secondary' onClick={() => navigateToActivity('location', locationId)}>
                                    View Activity
                                </Button>
                            </Stack>
                        ) : undefined
                    }
                />

                {/* Location Details Card */}
                <InfoCard title='Location Details' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2.5}>
                        {operation === Operation.Edit && (
                            <ReadOnlyField
                                label='Location ID'
                                value={getIdDisplayValue(data?.LocationID)}
                                size={6}
                                mono
                            />
                        )}

                        <DraftTextField
                            label='Location Name'
                            stateKey='LocationName'
                            draft={draft}
                            disabled={operation === Operation.View}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid location name',
                            }}
                            size={operation === Operation.Edit ? 6 : 12}
                            required
                        />

                        {/* Company */}
                        <DraftAutocompleteField<LocationDraft>
                            label='Company'
                            idKey='CompanyID'
                            nameKey='CompanyName'
                            draft={draft as any}
                            options={companyOptions}
                            disabled={operation === Operation.View || operation === Operation.Edit}
                            required
                            size={6}
                            onChange={() => {
                                // Reset reseller when company changes
                                setDraftState((prev) => ({ ...prev, ResellerID: null, ResellerName: '' } as any));
                            }}
                        />

                        {/* Reseller */}
                        <DraftAutocompleteField<LocationDraft>
                            label='Reseller'
                            idKey='ResellerID'
                            nameKey='ResellerName'
                            draft={draft as any}
                            options={resellerOptions}
                            disabled={operation === Operation.View || (operation === Operation.Edit && !role.isSuperAdmin)}
                            size={6}
                        />

                        {/* Country */}
                        <DraftAutocompleteField
                            label='Country'
                            idKey='Country'
                            draft={draft}
                            options={countryOptions}
                            disabled={operation === Operation.View}
                            loading={countriesLoading}
                            required
                            size={6}
                        />

                        {/* Tracking Type */}
                        <DraftSelectField
                            label='Tracking Type'
                            stateKey='TrackingFlag'
                            draft={draft}
                            disabled={operation === Operation.View}
                            options={TRACKING_TYPE_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                            }))}
                            size={6}
                        />

                        {/* PIN Input */}
                        <DraftPinField
                            draft={draft}
                            disabled={operation === Operation.View}
                            showLock={operation === Operation.Edit}
                            size={12}
                        />

                        {/* Consent to Sell */}
                        {role.isAdmin && role.prices.edit && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FormControlLabel
                                        sx={{ userSelect: 'none', ml: 0 }}
                                        checked={cts}
                                        onChange={({ target }) => {
                                            const checked = (target as HTMLInputElement).checked;
                                            setCts(checked);
                                            if (!checked) {
                                                setDraftState((prev) => ({ ...prev, ConsentToSell: null }));
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                sx={{
                                                    color: colors.neutral[400],
                                                    '&.Mui-checked': {
                                                        color: colors.primary[500],
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant='body2'
                                                sx={{ color: colors.neutral[700], fontWeight: 500 }}>
                                                Consent To Sell Agreement
                                            </Typography>
                                        }
                                    />

                                    {cts && (
                                        <SingleDatePicker
                                            date={
                                                draftState.ConsentToSell &&
                                                dayjs.tz(draftState.ConsentToSell).toISOString()
                                            }
                                            setDate={(ConsentToSell) =>
                                                setDraftState((prev) => ({ ...prev, ConsentToSell }))
                                            }
                                        />
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Systems at Location */}
                {operation !== Operation.Create && locationId && <LocationSystems locationId={locationId} />}

                {/* Audit Log */}
                {auditPk && operation !== Operation.Create && role.isAdmin && auditData.length > 0 && (
                    <ActivityCard audits={auditData as any} entityPk={auditPk} maxItems={5} />
                )}
            </EntityPageLayout>

            {/* Archive/Restore Confirmation Dialog */}
            <ConfirmDialog
                open={archiveDialogOpen}
                onClose={() => setArchiveDialogOpen(false)}
                onConfirm={handleArchiveToggle}
                title={draftState.Inactive ? 'Restore Location' : 'Archive Location'}
                message={
                    draftState.Inactive
                        ? `Are you sure you want to restore "${draftState.LocationName}"? This location will become visible in the active locations list.`
                        : `Are you sure you want to archive "${draftState.LocationName}"? Archived locations are hidden from the main list but can be restored later.`
                }
                confirmText={draftState.Inactive ? 'Restore' : 'Archive'}
                variant={draftState.Inactive ? 'success' : 'warning'}
                loading={mutationLoading}
            />
        </>
    );
};

export default LocationPanel;
